import React, { useState, useMemo } from 'react';
import { PhysicianType } from '../../../../services/ConfigurationService/types/physicianType';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { EditPhysicianDialog } from '../dialogs/EditPhysicianDialog';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface PhysicianGridProps {
  physicians: PhysicianType[];
}

interface PhysicianGridRow {
  id: string;
  code: string;
  firstName: string;
  lastName: string;
  specialtyName: string;
  npi: string;
}

export function PhysicianGrid({ physicians }: PhysicianGridProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPhysician, setSelectedPhysician] = useState<PhysicianType | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  // Filter physicians based on search term
  const filteredPhysicians = useMemo(() => {
    if (!searchTerm.trim()) {
      return physicians;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return physicians.filter(physician => 
      (physician.firstName?.toLowerCase().includes(searchLower)) ||
      (physician.lastName?.toLowerCase().includes(searchLower)) ||
      (physician.npi?.toLowerCase().includes(searchLower)) ||
      (physician.specialtyName?.toLowerCase().includes(searchLower)) ||
      (physician.code?.toLowerCase().includes(searchLower))
    );
  }, [physicians, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle row selection for editing
  const handleRowSelection = (args: RowSelectEventArgs) => {
    if (args.data) {
      const selectedRow = args.data as PhysicianGridRow;
      const physician = physicians.find(p => p.id === selectedRow.id);
      if (physician) {
        setSelectedPhysician(physician);
        // Trigger the dialog by clicking the hidden button
        setTimeout(() => {
          triggerRef.current?.click();
        }, 0);
      }
    }
  };

  const handleEditDialogClose = () => {
    setSelectedPhysician(null);
  };

  if (physicians.length === 0) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by physician name, NPI, specialty, code..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No physicians found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No physicians available for this center
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredPhysicians.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by physician name, NPI, specialty, code..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No physicians match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by physician name, NPI, specialty, code..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                }
              }
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ color: '#64748b' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Grid */}
      <GridComponent
        dataSource={filteredPhysicians}
        height={500}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 25 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}
        enableHover={true}
      >
          <ColumnsDirective>
            <ColumnDirective field="code" headerText="Code" width="100" textAlign="Left" />
            <ColumnDirective field="firstName" headerText="First Name" width="120" textAlign="Left" />
            <ColumnDirective field="lastName" headerText="Last Name" width="120" textAlign="Left" />
            <ColumnDirective field="specialtyName" headerText="Specialty" width="140" textAlign="Left" />
            <ColumnDirective field="npi" headerText="NPI" width="120" textAlign="Left" />
          </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>

      {/* Edit Dialog */}
      {selectedPhysician && (
        <EditPhysicianDialog
          physician={selectedPhysician}
          trigger={
            <button 
              ref={triggerRef}
              style={{ display: 'none' }}
              aria-hidden="true"
            />
          }
        />
      )}
    </Box>
  );
}
